import { Book } from 'epubjs';

import { PageListItem } from 'epubjs/types/pagelist';
import { NavItem, LandmarkItem } from 'epubjs/types/navigation';
import {
  EpubJsManifestItem,
  EpubJsPackagingSpineItem,
  Direction,
  EpubJsSpineItem,
  SmilItem,
  Layout,
} from '../types/epubjs';

export type Landmark = { type: string; href: string; index: number };

export type Manifest = {
  title: string;
  creator: string;
  direction: Direction;
  spread?: string;
  orientation?: string;
  spineItems: { [id: string]: EpubJsSpineItem };
  smilItems: { [id: string]: SmilItem };
  pages?: PageListItem[];
  toc?: NavItem[];
  landmarks: Landmark[];
};

const SMIL_TYPE = 'application/smil+xml';

const spineItemLayout = (properties: string[], defaultLayout: Layout): Layout => {
  if (properties.includes('rendition:layout-pre-paginated')) {
    return 'pre-paginated';
  }
  if (properties.includes('rendition:layout-reflowable')) {
    return 'reflowable';
  }

  return defaultLayout;
};

const getItems = (book: Book, defaultLayout: Layout): Pick<Manifest, 'spineItems' | 'smilItems'> => {
  const smilItems = Object.entries(book.packaging.manifest)
    .filter(([_, item]) => item.type === SMIL_TYPE)
    .map(([id, item]) => ({ id, path: item.href }))
    .reduce((a, v: SmilItem) => ({ ...a, [v.id]: v }), {} as Manifest['smilItems']);

  const spineItems = (book.packaging.spine as EpubJsPackagingSpineItem[])
    .map((item) => {
      const manifestItem: EpubJsManifestItem = book.packaging.manifest[item.idref];

      const layout = spineItemLayout(item.properties, defaultLayout);
      let overlay: SmilItem | undefined;

      if (manifestItem.overlay && smilItems[manifestItem.overlay]) {
        overlay = smilItems[manifestItem.overlay];
      }

      return {
        id: item.idref,
        index: item.index,
        path: manifestItem.href,
        layout,
        overlay,
        cfi: `epubcfi(${item.cfiBase}!/4/2)`,
      };
    })
    .reduce((a, v) => ({ ...a, [v.id]: v }), {} as Manifest['spineItems']);

  return {
    spineItems,
    smilItems,
  };
};

const parsePageList = (book: Book): PageListItem[] => {
  return (book.pageList as any)?.pageList as PageListItem[] | [];
};

const parseTableOfContents = (book: Book): NavItem[] => {
  return book.navigation.toc as NavItem[] | [];
};

const parseLandmarks = (book: Book): Landmark[] => {
  const raw: LandmarkItem[] = book.navigation?.landmarks ?? [];
  const landmarks: Landmark[] = [];
  for (const lm of raw) {
    if (!lm.href || !lm.type) continue;
    const spineItem = (book.spine as any)?.get?.(lm.href);
    if (!spineItem) continue;
    landmarks.push({ type: lm.type, href: lm.href, index: spineItem.index });
  }
  // Sorted by spine position — use-landmarks-debug's activeLandmarkAt walks this list backwards and
  // depends on that order. Do not remove.
  landmarks.sort((a, b) => a.index - b.index);
  return landmarks;
};

export const parse = (book: Book): Manifest => {
  const defaultLayout = (book.packaging.metadata.layout as Layout | undefined) || 'reflowable';
  // @ts-ignore
  const direction = book.packaging.metadata.direction === 'rtl' ? 'rtl' : 'ltr';
  // @ts-ignore - spread and orientation metadata from EPUB
  const spread = book.packaging.metadata.spread as string | undefined;
  // @ts-ignore - orientation metadata from EPUB
  const orientation = book.packaging.metadata.orientation as string | undefined;

  const items = getItems(book, defaultLayout);

  const pages = parsePageList(book);
  const toc = parseTableOfContents(book);
  const landmarks = parseLandmarks(book);

  return {
    title: book.packaging.metadata.title,
    creator: book.packaging.metadata.creator,
    direction,
    spread,
    orientation,
    ...items,
    pages,
    toc,
    landmarks,
  };
};
